// @flow
import { of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { push } from 'react-router-redux';
import { setNewAlert } from '../alert/duck';
import { postFlightApi } from './api';
import { ofType, StateObservable } from 'redux-observable';
import analytics from '../../analytics';

import type { Observable } from 'rxjs';
import type { HTTPError } from '../../error';
import type { Flight } from '../../entities';
import type { RootState } from '../index';

type NewFlightAction = {
  +type: string;
  +flightDate?: string;
  +departureCountry?: string;
  +departureAdmin1?: number;
  +departureAdmin2?: number;
  +arrivalCountry?: string;
  +arrivalAdmin1?: number;
  +arrivalAdmin2?: number;
  +flight?: Flight;
  +error?: HTTPError;
};

export type NewFlightState = {
  +isPosting?: boolean;
  +postError?: HTTPError;
};

// Actions
const NEW_FLIGHT_POST_FLIGHT_REQUEST = 'NEW_FLIGHT_POST_FLIGHT_REQUEST';
const NEW_FLIGHT_POST_FLIGHT_SUCCESS = 'NEW_FLIGHT_POST_FLIGHT_SUCCESS';
const NEW_FLIGHT_POST_FLIGHT_ERROR = 'NEW_FLIGHT_POST_FLIGHT_ERROR';

// Action Creators
export function postFlight(flightDate: string, departureCountry: string, departureAdmin1: number,
                           departureAdmin2: number, arrivalCountry: string, arrivalAdmin1: number,
                           arrivalAdmin2: number): NewFlightAction {
  return {
    type: NEW_FLIGHT_POST_FLIGHT_REQUEST,
    flightDate,
    departureCountry,
    departureAdmin1,
    departureAdmin2,
    arrivalCountry,
    arrivalAdmin1,
    arrivalAdmin2,
  };
}

export function postFlightSuccess(flight: Flight): NewFlightAction {
  return {
    type: NEW_FLIGHT_POST_FLIGHT_SUCCESS,
    flight,
  };
}

export function postFlightError(error: HTTPError): NewFlightAction {
  return {
    type: NEW_FLIGHT_POST_FLIGHT_ERROR,
    error,
  }
}

export const actions = {
  NEW_FLIGHT_POST_FLIGHT_REQUEST,
  NEW_FLIGHT_POST_FLIGHT_SUCCESS,
  NEW_FLIGHT_POST_FLIGHT_ERROR,
  postFlight,
  postFlightSuccess,
  postFlightError,
};

export const initialState: NewFlightState = {};

// Reducer
export default function reducer(state: NewFlightState = initialState,
                                action: NewFlightAction): NewFlightState {
  switch (action.type) {
    case NEW_FLIGHT_POST_FLIGHT_REQUEST:
      return {
        isPosting: true,
      };
    case NEW_FLIGHT_POST_FLIGHT_SUCCESS:
      return initialState;
    case NEW_FLIGHT_POST_FLIGHT_ERROR:
      return {
        postError: action.error,
      };
    default:
      return state;
  }
}

// Epics
export function postFlightEpic(action$: Observable<NewFlightAction>,
                               state$: StateObservable<RootState>): Observable<NewFlightAction> {
  return action$.pipe(
    ofType(NEW_FLIGHT_POST_FLIGHT_REQUEST),
    mergeMap(({
                flightDate, departureCountry, departureAdmin1, departureAdmin2,
                arrivalCountry, arrivalAdmin1, arrivalAdmin2,
              }) => {
      const req: any = {
        flightDate,
        departureCountry,
        arrivalCountry,
      };
      if (departureAdmin1) {
        req.departureAdmin1 = departureAdmin1;
      }
      if (departureAdmin2) {
        req.departureAdmin2 = departureAdmin2;
      }
      if (arrivalAdmin1) {
        req.arrivalAdmin1 = arrivalAdmin1;
      }
      if (arrivalAdmin2) {
        req.arrivalAdmin2 = arrivalAdmin2;
      }
      return postFlightApi(req).pipe(
        mergeMap((flight: Flight) => {
          return of(
            postFlightSuccess(flight),
            push(`/flight/${flight.id}/requests`),
          );
        }),
        catchError((e: HTTPError) => {
          if (Math.round(e.code / 100) === 5) {
            return of(postFlightError(e),
              setNewAlert('new-flight', 'error', undefined, 'httpErrors.post.500'),
            );
          } else if (Math.round(e.code / 100) === 4) {
            if (e.code === 401) {
              return of(postFlightError(e),
                setNewAlert('new-flight', 'error', undefined, 'httpErrors.post.401'),
              );
            }
            if (e.code === 400 && e.data && e.data.name === 'ExistingSimilarFlightError') {
              return of(postFlightError(e),
                setNewAlert('new-flight', 'error', undefined, 'newFlight.error.400.similarFlight'),
              );
            }
            return of(postFlightError(e),
              setNewAlert('new-flight', 'error', undefined, 'httpErrors.post.400'),
            );
          }
          return of(postFlightError(e))
        }))
    }));
}

/*
export function redirectAfterNewFlight(action$: ActionsObservable<NewFlightAction>) {
  return action$.pipe(
ofType(NEW_FLIGHT_POST_FLIGHT_SUCCESS)
    .mergeMap(({ flight }) => Observable.of(push(`/flight/${flight.id}/requests`)));
}
*/



// WEBPACK FOOTER //
// ./src/modules/new-flight/duck.js